import { CreateCourse, DeleteCourse, GetChannelTracks, GetCourseForEvent, UpdateCourse } from '@/connection/courses';
import { FetchError } from '@/connection/helpers';
import { useAppSelector } from '@/store/hooks';
import { getWorkingProject } from '@/store/slices/project/selectors';
import { getUser } from '@/store/slices/user/selectors';
import { ICourse } from '@/types/courses';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';
import { useCallback, useEffect, useRef, useState } from 'react';

// TODO: drop this entire file, use redux instead, and load redux from a loader rather than in a useEffect
// which is an antipattern - no more data loading in useEffect!
const useProjectCourse = () => {
  const user = useAppSelector(getUser);
  const workingEvent = useAppSelector(getWorkingProject);
  const savingRef = useRef(false);
  const loadingRef = useRef(false);
  const abortController = useRef<AbortController | null>(null);

  const [course, setCourse] = useState<ICourse | null>(null);
  const [working, setWorking] = useState(false);
  const fetchedKey = useRef<string | null>(null);

  const workingEventUuid = workingEvent?.uuid;
  const workingEventChannel = workingEvent?.channel;
  const courseLoaded = !!course;

  useEffect(() => {
    const currentKey = `${workingEventUuid}-${workingEventChannel}`;
    const shouldFetch =
      workingEventUuid &&
      workingEventChannel &&
      !courseLoaded &&
      !loadingRef.current &&
      fetchedKey.current !== currentKey;

    if (shouldFetch) {
      (async () => {
        try {
          fetchedKey.current = currentKey;
          setWorking(true);
          loadingRef.current = true;
          const [course] = await Promise.all([
            GetCourseForEvent(workingEventChannel, workingEventUuid),
            GetChannelTracks(workingEventChannel, undefined, 'courses'),
          ]);
          setCourse(course);
        } catch (e: unknown) {
          if (e instanceof FetchError && e.response && e.response.status === 404) {
            console.info('Course not found for event, creating new course');
          } else {
            toastTrigger('Course failed to load', {
              type: EToastTypes.error,
              autoClose: 5000,
            });
          }
        } finally {
          loadingRef.current = false;
          setWorking(false);
        }
      })();
    }
  }, [workingEventUuid, workingEventChannel, courseLoaded]);

  const handleSave = useCallback(
    async (name?: string, description?: string, categories?: string[], credits?: number) => {
      if (!user?.active_channel || !course) return;
      if (savingRef.current && abortController.current) {
        abortController.current.abort(`AbortCourse`);
      }

      savingRef.current = true;
      setWorking(true);
      const prevCourse = structuredClone(course);
      try {
        const newCourse = {
          ...course,
          ...(name && { name: { ...course.name, base: name } }),
          ...(description && { description }),
          ...(categories && { categories }),
          ...(credits && { credits }),
        };
        // optimistically update course state
        setCourse(newCourse);

        // setup abort controller
        abortController.current = new AbortController();
        const abortSignal = abortController.current.signal;

        // send save to DB with abort signal that can be aborted if a concurrent save is requested
        await UpdateCourse(user.active_channel, course.uuid, newCourse, abortSignal);

        // not setting working to false in finally clause because we don't want
        // an aborted request to set working to false because another save request
        // has come in and should resolve without errors.
        setWorking(false);

        // cancel saving and abort controller
        abortController.current = null;
        savingRef.current = false;
      } catch (e) {
        if (e instanceof FetchError && e.message.includes('AbortCourse')) {
          console.warn('Course save request aborted but likely completed');
          // leave working as true to not overwrite the value to false because another incoming save
          // has come through
          return;
        }

        console.error(e);
        toastTrigger('Error updating course', {
          type: EToastTypes.error,
          autoClose: 5000,
        });

        // not setting working to false in finally clause because we don't want
        // an aborted request to set working to false because another save request
        // has come in and should resolve without errors.
        setWorking(false);
        // save was a complete failure, revert visual changes
        setCourse(prevCourse);

        // cancel saving and reset abort controller
        abortController.current = null;
        savingRef.current = false;
      }
    },
    [course, user?.active_channel]
  );

  const handleDelete = useCallback(async () => {
    if (!user?.active_channel || !course) return;

    setWorking(true);
    try {
      await DeleteCourse(user.active_channel, course.uuid);
      setCourse(null);
    } catch (e) {
      console.error(e);
      toastTrigger('Error deleting course', {
        type: EToastTypes.error,
        autoClose: 5000,
      });
    } finally {
      setWorking(false);
    }
  }, [course, user?.active_channel]);

  const courseCanBeAddedToEvent = useCallback((): boolean => {
    const coursesCanbeAdded = !!workingEvent && workingEvent.sessions.length === 1;
    if (!coursesCanbeAdded) {
      toastTrigger('Course Project', {
        type: EToastTypes.warning,
        autoClose: 5000,
        description: 'Courses are limited to single session projects',
      });
    }

    return coursesCanbeAdded;
  }, [workingEvent]);

  const initializeCourse = useCallback(async () => {
    if (!user || !workingEvent || !courseCanBeAddedToEvent()) return;

    setWorking(true);
    try {
      const newCourse = await CreateCourse(user.active_channel, {
        event_id: workingEvent.event,
        channel: user.active_channel,
        name: {
          base: '',
          changed: '',
        },
        created_by: 0, // Backend will handle created_by, just required by typescript
        credits: 0,
        categories: [],
      });

      setCourse(newCourse);
    } catch {
      toastTrigger('Error initializing new course', {
        type: EToastTypes.error,
        autoClose: 5000,
      });
    } finally {
      setWorking(false);
    }
  }, [user, workingEvent, courseCanBeAddedToEvent]);

  return {
    course,
    setCourse,
    working,
    workingEvent,
    handleSave,
    handleDelete,
    courseCanBeAddedToEvent,
    initializeCourse,
  };
};

export default useProjectCourse;
